import React, { useState } from 'react';
import { Badge, Button, Form } from 'react-bootstrap';
import { AlertTriangle, Check, Edit2, Wrench, X } from 'lucide-react';
import {
    WorkOrderPriority,
    WorkOrderStatus,
    workOrderPriorityColors,
    workOrderPriorityLabels
} from '../../../../types/workOrders';
import { translateWorkOrderStatus } from '../../../../types/EnumTranslations';
import { formatDateOnlyForStorage } from '../../../../Helpers/DateUtils';
import { EditablePriorityBadge, PriorityOption } from '../../../../Components/EditablePriorityBadge';
import { WORK_ORDER_LABELS, workOrderSectionTestId } from '../../workOrderSections';
import './shared.css';

export interface WorkOrderBadgesProps {
    priority: WorkOrderPriority;
    status: WorkOrderStatus;
    /** YYYY-MM-DD or YYYY-MM-DD hh:mm:ss */
    dueDate?: string;
    /** Slot for a source badge (e.g. "Felanmälan"), rendered after the entity badge */
    sourceBadge?: React.ReactNode;
    /** When given the priority pill opens a priority menu on click */
    onChangePriority?: (priority: WorkOrderPriority) => Promise<void> | void;
}

export interface WorkOrderHeaderProps extends WorkOrderBadgesProps {
    title: string;
    /** When given the title gets a pencil button toggling inline editing */
    onSaveTitle?: (title: string) => Promise<void> | void;
    /** False when the badges render elsewhere (the admin modal's title bar) */
    showBadges?: boolean;
}

const PRIORITY_OPTIONS: PriorityOption<WorkOrderPriority>[] = [
    WorkOrderPriority.HIGH,
    WorkOrderPriority.MEDIUM,
    WorkOrderPriority.LOW
].map((p) => ({ value: p, label: workOrderPriorityLabels[p], variant: workOrderPriorityColors[p] }));

/** Öppen is the default state and gets no pill; the others do. */
const STATUS_PILL: Partial<Record<WorkOrderStatus, string>> = {
    [WorkOrderStatus.COMPLETED]: 'wo-status-pill--completed',
    [WorkOrderStatus.CANCELLED]: 'wo-status-pill--cancelled'
};

/** True when `dueDate` (date part) is strictly before today in Stockholm time. */
export function isWorkOrderOverdue(status: WorkOrderStatus, dueDate?: string): boolean {
    if (status !== WorkOrderStatus.OPEN || !dueDate) return false;
    const datePart = dueDate.split(' ')[0];
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return false;
    return datePart < formatDateOnlyForStorage(new Date());
}

/**
 * The pill row: ÄRENDE, priority, status (when not Öppen) and Försenad, in
 * the app's pill language (App.css).
 */
export const WorkOrderBadges: React.FC<WorkOrderBadgesProps> = ({
    priority,
    status,
    dueDate,
    sourceBadge,
    onChangePriority
}) => {
    const overdue = isWorkOrderOverdue(status, dueDate);
    const statusPill = STATUS_PILL[status];
    return (
        <div className="wo-header__badges">
            <Badge bg="" className="badge-work-order d-inline-flex align-items-center gap-1">
                <Wrench size={10} aria-hidden="true" />
                {WORK_ORDER_LABELS.entityBadge}
            </Badge>
            {sourceBadge}
            <EditablePriorityBadge
                value={priority}
                options={PRIORITY_OPTIONS}
                canEdit={!!onChangePriority}
                onChange={async (next) => { await onChangePriority?.(next); }}
                className="priority-badge flex-shrink-0"
            />
            {statusPill && (
                <Badge bg="" className={`wo-status-pill ${statusPill}`}>{translateWorkOrderStatus(status)}</Badge>
            )}
            {overdue && (
                <Badge bg="" className="badge-overdue gap-1">
                    <AlertTriangle size={10} aria-hidden="true" />
                    {WORK_ORDER_LABELS.overdue}
                </Badge>
            )}
        </div>
    );
};

export const WorkOrderHeader: React.FC<WorkOrderHeaderProps> = ({
    title,
    priority,
    status,
    dueDate,
    sourceBadge,
    onChangePriority,
    onSaveTitle,
    showBadges = true
}) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(title);
    const [saving, setSaving] = useState(false);

    const startEditing = () => {
        setDraft(title);
        setEditing(true);
    };

    const cancelEditing = () => {
        setEditing(false);
        setDraft(title);
    };

    const save = async () => {
        if (!onSaveTitle) return;
        const trimmed = draft.trim();
        if (!trimmed || trimmed === title) {
            cancelEditing();
            return;
        }
        setSaving(true);
        try {
            await onSaveTitle(trimmed);
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="wo-header mb-3" data-testid={workOrderSectionTestId('header')}>
            {showBadges && (
                <div className="mb-2">
                    <WorkOrderBadges
                        priority={priority}
                        status={status}
                        dueDate={dueDate}
                        sourceBadge={sourceBadge}
                        onChangePriority={onChangePriority}
                    />
                </div>
            )}

            {editing ? (
                <Form
                    className="d-flex flex-wrap align-items-center gap-2"
                    onSubmit={(e) => { e.preventDefault(); void save(); }}
                >
                    <Form.Control
                        type="text"
                        aria-label="Titel"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        disabled={saving}
                        autoFocus
                        className="flex-grow-1 wo-header__title-input"
                    />
                    <Button type="submit" size="sm" variant="primary" disabled={saving || !draft.trim()}>
                        <Check size={16} className="me-1" />Spara
                    </Button>
                    <Button type="button" size="sm" variant="outline-secondary" onClick={cancelEditing} disabled={saving}>
                        <X size={16} className="me-1" />Avbryt
                    </Button>
                </Form>
            ) : (
                <div className="d-flex align-items-center gap-2">
                    <h2 className="wo-header__title mb-0 flex-grow-1">{title}</h2>
                    {onSaveTitle && (
                        <Button
                            variant="link"
                            className="wo-edit-link wo-header__edit"
                            aria-label="Ändra titel"
                            onClick={startEditing}
                        >
                            <Edit2 size={12} aria-hidden="true" />
                            {WORK_ORDER_LABELS.edit}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

export default WorkOrderHeader;
